import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HardDrive, Music, Video, Image, User, ArrowLeft, RefreshCw, FileText, Trash2, AlertTriangle, X } from 'lucide-react';
import api from '../services/api';

const fmt = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const b = Number(bytes);
    if (b >= 1073741824) return (b / 1073741824).toFixed(2) + ' GB';
    if (b >= 1048576)    return (b / 1048576).toFixed(2) + ' MB';
    if (b >= 1024)       return (b / 1024).toFixed(1) + ' KB';
    return b + ' B';
};

const pct = (part, total) => total > 0 ? ((Number(part) / Number(total)) * 100).toFixed(1) : '0.0';

const TYPE_COLORS = {
    audio:   { bar: 'bg-purple-500', text: 'text-purple-400', icon: <Music className="w-4 h-4" /> },
    video:   { bar: 'bg-blue-500',   text: 'text-blue-400',   icon: <Video className="w-4 h-4" /> },
    image:   { bar: 'bg-pink-500',   text: 'text-pink-400',   icon: <Image className="w-4 h-4" /> },
    profile: { bar: 'bg-gray-500',   text: 'text-gray-400',   icon: <User className="w-4 h-4" /> },
    content: { bar: 'bg-green-500',  text: 'text-green-400',  icon: <FileText className="w-4 h-4" /> },
    cover:   { bar: 'bg-yellow-500', text: 'text-yellow-400', icon: <Image className="w-4 h-4" /> },
};

const Bar = ({ value, max, color = 'bg-blue-500' }) => (
    <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${Math.min((value / max) * 100, 100)}%` }} />
    </div>
);

const StatCard = ({ icon, label, value, sub, color = 'bg-blue-600', border = 'border-blue-500/30' }) => (
    <div className={`glass-card p-6 border ${border}`}>
        <div className="flex items-center justify-between mb-3">
            <div className={`p-3 ${color} rounded-lg`}>{icon}</div>
        </div>
        <div className="text-2xl font-bold text-white mb-1">{value}</div>
        <div className="text-gray-400 text-sm mb-1">{label}</div>
        {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
);

const ConfirmModal = ({ title, message, confirmLabel = 'Confirm', danger = true, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="glass-card border border-red-500/40 p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="text-white font-semibold text-lg mb-1">{title}</h3>
                    <p className="text-gray-400 text-sm">{message}</p>
                </div>
                <button onClick={onCancel} aria-label="Cancel" className="ml-auto text-gray-500 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="flex justify-end gap-3">
                <button onClick={onCancel} className="btn-secondary text-sm px-4 py-2">Cancel</button>
                <button
                    onClick={onConfirm}
                    className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${
                        danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    </div>
);

const AdminStorage = () => {
    const [tab, setTab]           = useState('storage');
    const [data, setData]         = useState(null);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [trash, setTrash]       = useState(null);
    const [trashLoading, setTrashLoading] = useState(false);
    const [confirm, setConfirm]   = useState(null); // { type: 'one'|'all', id?: number }
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast]       = useState(null);

    const showToast = (msg, ok = true) => {
        setToast({ msg, ok });
        setTimeout(() => setToast(null), 3500);
    };

    const loadStorage = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.get('/admin/storage');
            if (res.data.success) setData(res.data.data);
            else setError('Failed to load storage data');
        } catch (err) {
            console.error(err);
            setError('Unable to load storage stats');
        } finally {
            setLoading(false);
        }
    };

    const loadTrash = useCallback(async () => {
        try {
            setTrashLoading(true);
            const res = await api.get('/admin/storage/trash');
            if (res.data.success) setTrash(res.data.data);
        } catch (err) {
            console.error(err);
        } finally {
            setTrashLoading(false);
        }
    }, []);

    useEffect(() => { loadStorage(); }, []);
    useEffect(() => { if (tab === 'trash' && !trash) loadTrash(); }, [tab, trash, loadTrash]);

    const handleDeleteOne = async (id) => {
        setDeleting(true);
        try {
            const res = await api.delete(`/admin/storage/trash/${id}`);
            if (res.data.success) {
                setTrash(prev => ({
                    ...prev,
                    items: prev.items.filter(i => i.id !== id),
                    totalBytes: prev.items.filter(i => i.id !== id).reduce((s, i) => s + Number(i.file_size || 0), 0),
                }));
                showToast(
                    res.data.fileStatus === 'deleted'
                        ? 'File removed from disk and database.'
                        : 'Database record cleared (file was already gone from disk).'
                );
            } else {
                showToast(res.data.message || 'Failed to delete file.', false);
            }
        } catch (err) {
            showToast('Failed to delete file.', false);
        } finally {
            setDeleting(false);
            setConfirm(null);
        }
    };

    const handleDeleteAll = async () => {
        setDeleting(true);
        try {
            const res = await api.delete('/admin/storage/trash');
            if (res.data.success) {
                setTrash({ items: [], totalBytes: 0 });
                const { recordsDeleted = 0, filesDeleted = 0, filesNotFound = 0, fileErrors = 0 } = res.data;
                const msg = fileErrors > 0
                    ? `${recordsDeleted} records cleared — ${filesDeleted} files deleted, ${fileErrors} file(s) could not be removed (check server permissions).`
                    : `${recordsDeleted} records cleared — ${filesDeleted} files deleted from disk, ${filesNotFound} were already gone.`;
                showToast(msg, fileErrors === 0);
                // Refresh storage stats too
                loadStorage();
            }
        } catch (err) {
            showToast('Failed to empty trash.', false);
        } finally {
            setDeleting(false);
            setConfirm(null);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12 flex items-center justify-center">
            <div className="text-center">
                <HardDrive className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                <p className="text-gray-400">Loading storage data...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12 flex items-center justify-center">
            <div className="glass-card p-8 text-center border border-red-500/30 max-w-md">
                <p className="text-red-400 mb-4">{error}</p>
                <button onClick={loadStorage} className="btn-secondary">Try Again</button>
            </div>
        </div>
    );

    const { summary, byType, topUsers, largestFiles, monthlyGrowth } = data || {};
    const maxUserBytes  = Math.max(...(topUsers?.map(u => Number(u.bytes)) || [1]), 1);
    const maxMonthBytes = Math.max(...(monthlyGrowth?.map(m => Number(m.bytes_added)) || [1]), 1);

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">

                {/* Toast */}
                {toast && (
                    <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
                        toast.ok ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
                    }`}>
                        {toast.msg}
                    </div>
                )}

                {/* Confirm modal */}
                {confirm && (
                    <ConfirmModal
                        title={confirm.type === 'all' ? 'Empty Trash?' : 'Permanently Delete?'}
                        message={
                            confirm.type === 'all'
                                ? `This will permanently delete all ${trash?.items?.length || 0} items in the trash and remove any remaining files from the server. This cannot be undone.`
                                : 'This will permanently delete this file and remove it from the server. This cannot be undone.'
                        }
                        confirmLabel={confirm.type === 'all' ? 'Empty Trash' : 'Delete Forever'}
                        onConfirm={() => confirm.type === 'all' ? handleDeleteAll() : handleDeleteOne(confirm.id)}
                        onCancel={() => setConfirm(null)}
                    />
                )}

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Link to="/admin" className="text-gray-400 hover:text-white transition-colors">
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <HardDrive className="w-8 h-8 text-orange-400" />
                            <h1 className="text-4xl font-black gradient-text">Storage</h1>
                        </div>
                        <p className="text-gray-400 ml-16">Full breakdown of disk usage across all uploads</p>
                    </div>
                    <button
                        onClick={() => { loadStorage(); if (tab === 'trash') loadTrash(); }}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-8 border-b border-gray-700">
                    <button
                        onClick={() => setTab('storage')}
                        className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                            tab === 'storage'
                                ? 'border-orange-400 text-orange-400'
                                : 'border-transparent text-gray-400 hover:text-white'
                        }`}
                    >
                        <span className="flex items-center gap-2"><HardDrive className="w-4 h-4" /> Storage</span>
                    </button>
                    <button
                        onClick={() => setTab('trash')}
                        className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                            tab === 'trash'
                                ? 'border-red-400 text-red-400'
                                : 'border-transparent text-gray-400 hover:text-white'
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <Trash2 className="w-4 h-4" /> Trash
                            {trash?.items?.length > 0 && (
                                <span className="bg-red-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                                    {trash.items.length}
                                </span>
                            )}
                        </span>
                    </button>
                </div>

                {/* ── STORAGE TAB ── */}
                {tab === 'storage' && (
                    <>
                        {/* Summary cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <StatCard
                                icon={<HardDrive className="w-6 h-6 text-white" />}
                                label="Total Storage"
                                value={fmt(summary?.total_bytes)}
                                sub={`${summary?.total_files} files`}
                                color="bg-orange-600"
                                border="border-orange-500/30"
                            />
                            <StatCard
                                icon={<FileText className="w-6 h-6 text-white" />}
                                label="Content Storage"
                                value={fmt(summary?.content_bytes)}
                                sub={`${pct(summary?.content_bytes, summary?.total_bytes)}% of total · ${summary?.content_files} files`}
                                color="bg-green-600"
                                border="border-green-500/30"
                            />
                            <StatCard
                                icon={<User className="w-6 h-6 text-white" />}
                                label="Profile Photos"
                                value={fmt(summary?.profile_bytes)}
                                sub={`${pct(summary?.profile_bytes, summary?.total_bytes)}% of total`}
                                color="bg-gray-600"
                                border="border-gray-500/30"
                            />
                            <StatCard
                                icon={<Image className="w-6 h-6 text-white" />}
                                label="Cover Art"
                                value={fmt(summary?.cover_bytes)}
                                sub={`${pct(summary?.cover_bytes, summary?.total_bytes)}% of total`}
                                color="bg-yellow-600"
                                border="border-yellow-500/30"
                            />
                        </div>

                        {/* Type breakdown + Monthly growth */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                            <div className="glass-card p-6">
                                <h3 className="text-white font-semibold mb-4">Content by File Type</h3>
                                <div className="space-y-4">
                                    {byType?.map(row => {
                                        const c = TYPE_COLORS[row.file_type] || TYPE_COLORS.content;
                                        return (
                                            <div key={row.file_type}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className={c.text}>{c.icon}</span>
                                                        <span className="text-gray-200 text-sm capitalize">{row.file_type}</span>
                                                        <span className="text-gray-500 text-xs">{row.files} files</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-white text-sm font-medium">{fmt(row.bytes)}</span>
                                                        <span className="text-gray-500 text-xs ml-2">{pct(row.bytes, summary?.content_bytes)}%</span>
                                                    </div>
                                                </div>
                                                <Bar value={Number(row.bytes)} max={Number(summary?.content_bytes || 1)} color={c.bar} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="glass-card p-6">
                                <h3 className="text-white font-semibold mb-4">Monthly Growth (Content, Last 12 mo)</h3>
                                {monthlyGrowth?.length === 0 && <p className="text-gray-500 text-sm">No data yet</p>}
                                <div className="space-y-2">
                                    {monthlyGrowth?.map(row => (
                                        <div key={row.month} className="flex items-center gap-3">
                                            <span className="text-gray-500 text-xs w-16 flex-shrink-0">{row.month}</span>
                                            <div className="flex-1">
                                                <Bar value={Number(row.bytes_added)} max={maxMonthBytes} color="bg-orange-500" />
                                            </div>
                                            <div className="text-right flex-shrink-0 w-28">
                                                <span className="text-white text-xs font-medium">{fmt(row.bytes_added)}</span>
                                                <span className="text-gray-500 text-xs ml-1">· {row.files_added} files</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Top users */}
                        <div className="glass-card p-6 mb-6">
                            <h3 className="text-white font-semibold mb-4">Top Users by Storage</h3>
                            <div className="space-y-3">
                                {topUsers?.length === 0 && <p className="text-gray-500 text-sm">No data</p>}
                                {topUsers?.map((u, i) => (
                                    <div key={u.username} className="flex items-center gap-3">
                                        <span className="text-gray-500 text-xs w-5 flex-shrink-0">{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <Link to={`/profile/${u.username}`} className="text-sm text-blue-400 hover:text-blue-300 hover:underline transition-colors">
                                                    {u.display_name || u.username}
                                                </Link>
                                                <span className="text-gray-600 text-xs">@{u.username}</span>
                                                <span className="text-gray-600 text-xs">
                                                    {u.audio > 0 && `${u.audio}a `}{u.video > 0 && `${u.video}v `}{u.image > 0 && `${u.image}i`}
                                                </span>
                                            </div>
                                            <Bar value={Number(u.bytes)} max={maxUserBytes} color="bg-blue-500" />
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm text-white font-medium">{fmt(u.bytes)}</div>
                                            <div className="text-xs text-gray-500">{u.files} files</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Largest files */}
                        <div className="glass-card p-6">
                            <h3 className="text-white font-semibold mb-4">Largest Files</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-gray-500 text-xs border-b border-gray-700">
                                            <th className="text-left pb-3 pr-4">#</th>
                                            <th className="text-left pb-3 pr-4">Title</th>
                                            <th className="text-left pb-3 pr-4">User</th>
                                            <th className="text-left pb-3 pr-4">Type</th>
                                            <th className="text-right pb-3">Size</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {largestFiles?.map((f, i) => {
                                            const c = TYPE_COLORS[f.file_type] || TYPE_COLORS.content;
                                            const url = f.file_category !== 'profile' && f.username && f.stored_filename
                                                ? `/view/${f.file_type}/${f.username}/${f.stored_filename}`
                                                : null;
                                            return (
                                                <tr key={i} className="hover:bg-gray-800/30 transition-colors">
                                                    <td className="py-3 pr-4 text-gray-500">{i + 1}</td>
                                                    <td className="py-3 pr-4 max-w-xs">
                                                        {url ? (
                                                            <a href={url} target="_blank" rel="noopener noreferrer"
                                                                className="text-blue-400 hover:text-blue-300 hover:underline truncate block transition-colors">
                                                                {f.title || f.stored_filename || 'Untitled'}
                                                            </a>
                                                        ) : (
                                                            <span className="text-gray-300 truncate block">{f.title || f.stored_filename || 'Profile photo'}</span>
                                                        )}
                                                        <span className="text-gray-600 text-xs capitalize">{f.file_category}</span>
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <Link to={`/profile/${f.username}`} className="text-gray-300 hover:text-white transition-colors">
                                                            @{f.username}
                                                        </Link>
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <span className={`flex items-center gap-1 ${c.text} capitalize`}>{c.icon} {f.file_type}</span>
                                                    </td>
                                                    <td className="py-3 text-right text-white font-medium">{fmt(f.file_size)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* ── TRASH TAB ── */}
                {tab === 'trash' && (
                    <div className="glass-card border border-red-500/20 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                    <Trash2 className="w-5 h-5 text-red-400" /> Trash
                                </h3>
                                {trash && (
                                    <p className="text-gray-500 text-sm mt-1">
                                        {trash.items.length} items · {fmt(trash.totalBytes)} recoverable
                                    </p>
                                )}
                            </div>
                            {trash?.items?.length > 0 && (
                                <button
                                    disabled={deleting}
                                    onClick={() => setConfirm({ type: 'all' })}
                                    className="flex items-center gap-2 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" /> Empty Trash
                                </button>
                            )}
                        </div>

                        {trashLoading && (
                            <div className="text-center py-12">
                                <Trash2 className="w-10 h-10 text-gray-600 mx-auto mb-3 animate-pulse" />
                                <p className="text-gray-400">Loading trash...</p>
                            </div>
                        )}

                        {!trashLoading && trash?.items?.length === 0 && (
                            <div className="text-center py-16 text-gray-500">
                                <Trash2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-lg font-medium mb-1">Trash is empty</p>
                                <p className="text-sm">Deleted content will appear here</p>
                            </div>
                        )}

                        {!trashLoading && trash?.items?.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-gray-500 text-xs border-b border-gray-700">
                                            <th className="text-left pb-3 pr-4">File</th>
                                            <th className="text-left pb-3 pr-4">User</th>
                                            <th className="text-left pb-3 pr-4">Type</th>
                                            <th className="text-left pb-3 pr-4">Deleted</th>
                                            <th className="text-right pb-3 pr-4">Size</th>
                                            <th className="text-right pb-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {trash.items.map(f => {
                                            const c = TYPE_COLORS[f.file_type] || TYPE_COLORS.content;
                                            return (
                                                <tr key={f.id} className="hover:bg-gray-800/30 transition-colors group">
                                                    <td className="py-3 pr-4 max-w-xs">
                                                        <div className="text-gray-300 truncate">
                                                            {f.title || f.original_filename || 'Untitled'}
                                                        </div>
                                                        <div className="text-gray-600 text-xs capitalize">{f.file_category}</div>
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <Link to={`/profile/${f.username}`} className="text-gray-400 hover:text-white transition-colors text-xs">
                                                            @{f.username}
                                                        </Link>
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <span className={`flex items-center gap-1 ${c.text} capitalize text-xs`}>
                                                            {c.icon} {f.file_type}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 pr-4 text-gray-500 text-xs">
                                                        {new Date(f.updated_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="py-3 pr-4 text-right text-gray-300 text-xs font-medium">
                                                        {fmt(f.file_size)}
                                                    </td>
                                                    <td className="py-3 text-right">
                                                        <button
                                                            disabled={deleting}
                                                            onClick={() => setConfirm({ type: 'one', id: f.id })}
                                                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-900/30 transition-all disabled:opacity-30"
                                                            title="Permanently delete"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default AdminStorage;
